import mitt, { Emitter, Handler, WildcardHandler } from "mitt";
import { EventsWithContext } from "./types";

type Middleware<Context, EventType extends keyof any> = (context: Context, type: EventType) => Context;

export class BaseEventEmitter<Events extends Record<string, any>, Context = any> {
  protected emitter: Emitter<EventsWithContext<Events, Context>>;
  private middlewares: { [Key in keyof Events | "*"]?: Middleware<Context, any>[] } = {};

  constructor() {
    this.emitter = mitt<EventsWithContext<Events, Context>>();
  }

  use<Key extends keyof Events | "*">(
    type: Key,
    middleware: Middleware<Context, Key extends "*" ? string : Key>
  ): () => void {
    if (!this.middlewares[type]) {
      this.middlewares[type] = [];
    }
    this.middlewares[type]!.push(middleware);

    return () => {
      this.middlewares[type] = this.middlewares[type]!.filter((mw) => mw !== middleware);
    };
  }

  on<Key extends keyof Events | "*">(
    type: Key,
    handler: Key extends "*"
      ? WildcardHandler<EventsWithContext<Events, Context>>
      : Handler<EventsWithContext<Events, Context>[Extract<Key, keyof Events>]>
  ) {
    this.emitter.on(type as any, handler as any);

    return () => {
      this.emitter.off(type as any, handler as any);
    };
  }

  off<Key extends keyof Events | "*">(
    type: Key,
    handler: Key extends "*"
      ? WildcardHandler<EventsWithContext<Events, Context>>
      : Handler<EventsWithContext<Events, Context>[Extract<Key, keyof Events>]>
  ): void {
    this.emitter.off(type as any, handler as any);
  }

  emit<Key extends keyof Events>(
    type: Key,
    eventData: Events[Key],
    context?: Context
  ): EventsWithContext<Events, Context>[Key] {
    let ctx = context || ({} as Context);

    const globalMiddlewares = this.middlewares["*"] || [];
    globalMiddlewares.forEach((middleware) => {
      ctx = middleware(ctx, type as string);
    });

    const eventMiddlewares = this.middlewares[type] || [];
    eventMiddlewares.forEach((middleware) => {
      ctx = middleware(ctx, type);
    });

    const dataWithContext = { context: ctx, payload: eventData };

    this.emitter.emit(type as any, dataWithContext);

    return dataWithContext;
  }
}
